import { provideReact } from "../../react/index.js";
import { DnsRecordListBase } from "../DnsRecordListBase/index.js";
import type { DnsRecordSettingsData } from "../DnsRecordSettings/index.js";
import type { DnsZoneCommon } from "../DnsZone/index.js";
import { DnsZone } from "../DnsZone/index.js";
import type { Ingress, IngressCommon } from "../Ingress/index.js";
import type { CnameListItem } from "./types.js";

export class DnsCnameRecordsList extends DnsRecordListBase<CnameListItem> {
  public constructor(
    ingress: IngressCommon,
    parentZone: DnsZoneCommon,
    items: CnameListItem[],
  ) {
    super(items, parentZone, ingress);
  }

  public static getByIngress = provideReact(
    async (ingress: Ingress, rootZone: DnsZoneCommon) => {
      const ingressDetailed = await ingress.getDetailed();
      const dnsZones = await DnsZone.query(ingressDetailed.project, {
        domain: ingressDetailed.hostname,
      }).execute();

      const items: CnameListItem[] = [];
      dnsZones.items.forEach((i) => {
        if (i.recordSet.cname.fqdn && i.domain.includes("_")) {
          items.push({
            domain: i.domain,
            zoneId: i.id,
            fqdn: i.recordSet.cname.fqdn,
            type: "unchanged",
          });
        }
      });

      return new DnsCnameRecordsList(ingressDetailed, rootZone, items);
    },
  );

  public addItem(item: CnameListItem): CnameListItem[] {
    this.items.push({ ...item, type: "new" });
    return this.items;
  }

  public updateItem(
    oldCname: CnameListItem,
    newCname: CnameListItem,
  ): CnameListItem[] {
    if (oldCname.type === "new") {
      const updatedItem: CnameListItem = {
        ...newCname,
        type: "new",
      };
      this.items = this.items.map((i) =>
        i.domain === oldCname.domain ? updatedItem : i,
      );
      return this.items;
    }

    if (oldCname.domain !== newCname.domain) {
      this.deleteItem(oldCname);
      return this.addItem(newCname);
    }

    const updatedItem: CnameListItem = {
      ...newCname,
      type: "changed",
    };
    this.items = this.items.map((i) =>
      i.domain === oldCname.domain ? updatedItem : i,
    );
    return this.items;
  }

  public deleteItem(cname: CnameListItem): CnameListItem[] {
    if (cname.type === "new") {
      this.items = this.items.filter((i) => i.domain === cname.domain);
      return this.items;
    }

    this.items = this.items.map((i) =>
      i.domain === cname.domain ? { ...i, type: "deleted" } : i,
    );
    return this.items;
  }

  public async saveItems(settings: DnsRecordSettingsData) {
    for (const item of this.items) {
      if (item.type === "unchanged") {
        continue;
      }
      if (item.type === "changed" && item.zoneId) {
        await DnsZone.ofId(item.zoneId).setCname(item.fqdn, settings);
        continue;
      }
      if (item.type === "deleted" && item.zoneId) {
        await DnsZone.ofId(item.zoneId).setCname();
        continue;
      }
      const existingZoneQuery = await DnsZone.query(this.ingress.project, {
        domain: item.domain,
      }).execute();
      const existingZone = existingZoneQuery.items.find(
        (i) => i.domain === item.domain,
      );
      if (existingZone) {
        await existingZone.setCname(item.fqdn, settings);
        continue;
      }
      const newZone = await DnsZone.create(
        this.getPrefix(item.domain),
        this.parentZone.id,
      );
      await newZone.setCname(item.fqdn, settings);
    }
  }
}
